import { firstPublicUserId, sanitizeCustomerUserData } from "./customerIdentity";
import { rememberVpnTestAccess } from "./vpnTestAccess";

export const CUSTOMER_TOKEN_KEY = "pw_customer_access_token";
export const CUSTOMER_TOKEN_EXPIRES_KEY = "pw_customer_access_token_expires_at";
export const CUSTOMER_AUTH_CHANGED_EVENT = "pw-customer-auth-changed";

function getLegacyCustomerAccessToken() {
  const stored = String(localStorage.getItem("access") || "").trim();
  if (!stored) return "";
  try {
    const parsed = JSON.parse(stored);
    if (typeof parsed === "string") return parsed.trim();
    if (parsed && typeof parsed === "object") {
      return String(
        parsed.access_token ||
        parsed.accessToken ||
        parsed.token ||
        parsed.auth_token ||
        "",
      ).trim();
    }
    return "";
  } catch {
    return stored;
  }
}

export function notifyCustomerAuthChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CUSTOMER_AUTH_CHANGED_EVENT));
}

export function isCustomerTokenExpired() {
  const expiresAt = getCustomerTokenExpiresAt();
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

export function clearExpiredCustomerSession() {
  if (isCustomerTokenExpired()) {
    clearCustomerSession();
    return true;
  }
  return false;
}

export function getCustomerAccessToken() {
  clearExpiredCustomerSession();
  return String(localStorage.getItem(CUSTOMER_TOKEN_KEY) || "").trim() || getLegacyCustomerAccessToken();
}

export function getCustomerTokenExpiresAt() {
  return String(localStorage.getItem(CUSTOMER_TOKEN_EXPIRES_KEY) || "").trim();
}

export function clearCustomerSession() {
  localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  localStorage.removeItem(CUSTOMER_TOKEN_EXPIRES_KEY);
  localStorage.removeItem("access");
  notifyCustomerAuthChanged();
}

export function getStoredUserData() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "null");
  } catch {
    return null;
  }
}

export function getCurrentPublicUserId() {
  const userData = getStoredUserData();
  return firstPublicUserId(userData);
}

export function persistCustomerSession(responseData = {}) {
  rememberVpnTestAccess(responseData);
  const nestedData = Array.isArray(responseData?.data) ? responseData.data[0] : responseData?.data;
  const sources = [
    responseData,
    responseData?.auth,
    responseData?.session,
    nestedData,
    nestedData?.auth,
    nestedData?.session,
  ].filter((source) => source && typeof source === "object");
  const token = String(sources.map((source) => (
    source.access_token
    || source.accessToken
    || source.token
    || source.auth_token
    || source.bearer_token
    || ""
  )).find(Boolean) || "").trim();
  const expiresAt = String(sources.map((source) => (
    source.expires_at
    || source.expiresAt
    || source.token_expires_at
    || source.tokenExpiresAt
    || ""
  )).find(Boolean) || "").trim();
  if (token) {
    localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
  }
  if (expiresAt) {
    localStorage.setItem(CUSTOMER_TOKEN_EXPIRES_KEY, expiresAt);
  }
  return token;
}

export function persistUserDataWithSession(responseData = {}, userData) {
  persistCustomerSession(responseData);
  const nextUserData = sanitizeCustomerUserData(userData, responseData);
  localStorage.setItem("userData", JSON.stringify(nextUserData));
  notifyCustomerAuthChanged();
  return nextUserData;
}

export function authHeaders(headers = {}) {
  const token = getCustomerAccessToken();
  return token
    ? { ...headers, Authorization: `Bearer ${token}` }
    : { ...headers };
}
